<script setup>
import { ldJson } from '@/lib/jsonLd';
import { Head } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { useCopy } from '@/composables/useCopy';

const props = defineProps({
    domain: Object, branding: Object, service: String, designVariant: Object,
    funnelVariant: Object, todayLeadCount: { type: Number, default: 0 },
    nearbyPartnerCount: { type: Number, default: 0 },
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) }, seoTitle: String,
    metaDescription: String, seoNoIndex: { type: Boolean, default: false },
    jsonLd: Object, breadcrumbJsonLd: Object,
});

const { t, ts, spot } = useCopy(props);

const isWP = computed(() => props.service === 'wp');
const serviceLabel = computed(() => isWP.value ? 'Wärmepumpe' : 'Photovoltaikanlage');
const brand = computed(() => props.domain?.settings || {});
const brandName = computed(() => brand.value.brand_name || 'StudiVolt');
const brandCta = computed(() => brand.value.brand_cta || 'Kostenlose Beratung starten');
const priceFrom = computed(() => isWP.value ? ts('product_price_from_wp', 'ab 12.500 EUR') : ts('product_price_from_pv', 'ab 8.900 EUR'));
const heroImageUrl = computed(() => {
    if (props.domain?.hero_image) return '/media/' + props.domain.hero_image.replace(/^\//, '');
    return props.spotImages?.product_hero || props.spotImages?.welcome_hero || '/images/hero-main.webp';
});
const benefits = computed(() => isWP.value ? [
    { title: t('product_benefit_1_title_wp', 'Heizlastberechnung'), desc: t('product_benefit_1_desc_wp', 'Persönliche Analyse deines Hauses und Systemauswahl.') },
    { title: t('product_benefit_2_title_wp', 'KfW-Förderung'), desc: t('product_benefit_2_desc_wp', 'Bis zu 70% Zuschuss - wir übernehmen den Antrag.') },
    { title: t('product_benefit_3_title_wp', 'Angebote vergleichen'), desc: t('product_benefit_3_desc_wp', 'Regionale Fachbetriebe, offene Konditionen.') },
    { title: t('product_benefit_4_title_wp', 'Schlüsselfertig'), desc: t('product_benefit_4_desc_wp', 'Montage, Abnahme, Inbetriebnahme - alles inklusive.') },
] : [
    { title: t('product_benefit_1_title_pv', 'Dachanalyse'), desc: t('product_benefit_1_desc_pv', 'Individuelle Ertragsberechnung für dein Dach.') },
    { title: t('product_benefit_2_title_pv', '0% MwSt.'), desc: t('product_benefit_2_desc_pv', 'Seit 2023 steuerfrei - wir erklären dir alles.') },
    { title: t('product_benefit_3_title_pv', 'Angebote vergleichen'), desc: t('product_benefit_3_desc_pv', 'Regionale Fachbetriebe, offene Konditionen.') },
    { title: t('product_benefit_4_title_pv', '25 Jahre Garantie'), desc: t('product_benefit_4_desc_pv', 'Hochleistungsmodule mit langfristiger Sicherheit.') },
]);

const showFunnelModal = ref(false);
const showFloatingCTA = ref(false);
const openFunnel = () => { showFunnelModal.value = true; document.body.style.overflow = 'hidden'; };
const closeFunnel = () => { showFunnelModal.value = false; document.body.style.overflow = ''; };
const handleScroll = () => { const d = document.documentElement.scrollHeight - (window.innerHeight + window.scrollY); showFloatingCTA.value = window.scrollY > 600 && d > 220; };
onMounted(() => window.addEventListener('scroll', handleScroll, { passive: true }));
onUnmounted(() => window.removeEventListener('scroll', handleScroll));
</script>

<template>
    <Head :title="seoTitle || `${serviceLabel} - ${brandName}`">
        <meta name="description" :content="metaDescription" />
        <script v-if="jsonLd" type="application/ld+json" v-html="ldJson(jsonLd)"></script>
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" heroMode>

        <!-- HERO: dark, full-bleed image, left-aligned -->
        <section class="sv-hero">
            <img :src="heroImageUrl" :alt="(isWP ? 'Wärmepumpe' : 'Photovoltaik') + ' — Beratung, Planung und Installation vom Fachbetrieb'" class="sv-hero__bg" loading="eager" />
            <div class="sv-hero__overlay"></div>
            <div class="sv-hero__inner">
                <div class="sv-hero__content">
                    <p class="sv-hero__tag">{{ serviceLabel }} {{ priceFrom }}</p>
                    <h1 class="sv-hero__h1">{{ isWP ? ts('product_hero_h1_label_wp', 'Wärmepumpe') : ts('product_hero_h1_label_pv', 'Photovoltaik') }} {{ t('product_hero_h1_mid', 'mit') }} <span class="sv-hero__accent">{{ t('product_hero_h1_accent', 'persönlicher Beratung.') }}</span></h1>
                    <p class="sv-hero__sub">{{ t('product_hero_sub', 'Dein studentischer Berater begleitet dich 1:1 - von der ersten Frage bis zur fertigen Anlage.') }}</p>
                    <button type="button" @click="openFunnel" class="sv-btn sv-btn--accent">{{ brandCta }}</button>
                </div>
            </div>
        </section>

        <!-- BENEFITS: 2x2 grid cards with gradient border on hover -->
        <section class="sv-benefits">
            <div class="sv-container">
                <h2 class="sv-section-title">{{ isWP ? ts('product_benefits_heading_wp', 'Deine Wärmepumpen-Beratung') : ts('product_benefits_heading_pv', 'Deine Photovoltaik-Beratung') }}</h2>
                <div class="sv-benefits__grid">
                    <div v-for="(b, idx) in benefits" :key="idx" class="sv-benefits__card">
                        <span class="sv-benefits__num">{{ String(idx + 1).padStart(2, '0') }}</span>
                        <h3>{{ b.title }}</h3>
                        <p>{{ b.desc }}</p>
                    </div>
                </div>
                <div style="text-align: center; margin-top: 48px;">
                    <button type="button" @click="openFunnel" class="sv-btn sv-btn--accent">{{ brandCta }}</button>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="sv-cta-section">
            <div class="sv-container" style="text-align: center;">
                <h2>{{ ts('product_cta_heading', `${serviceLabel.value} - persönlich beraten lassen.`) }}</h2>
                <button type="button" @click="openFunnel" class="sv-btn sv-btn--dark">{{ brandCta }}</button>
            </div>
        </section>

        <!-- Modal -->
        <Transition name="sv-fade">
            <div v-if="showFunnelModal" class="sv-overlay" @click.self="closeFunnel">
                <div class="sv-dialog">
                    <button type="button" @click="closeFunnel" class="sv-dialog__x" :aria-label="t('product_dialog_close_label', 'Schließen')"><XMarkIcon class="w-5 h-5" /></button>
                    <p class="sv-dialog__title">{{ ts('product_dialog_title', `${serviceLabel.value} - Beratung starten`) }}</p>
                    <p class="sv-dialog__sub">{{ t('product_dialog_sub', 'Unverbindlich - in 2 Minuten') }}</p>
                    <LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" :funnel-variant="funnelVariant" :today-lead-count="todayLeadCount" :nearby-partner-count="nearbyPartnerCount" bare :dark-mode="false" />
                </div>
            </div>
        </Transition>
        <Transition name="sv-slide">
            <div v-if="showFloatingCTA" class="sv-sticky lg:hidden">
                <button type="button" @click="openFunnel" class="sv-btn sv-btn--accent sv-btn--full">{{ brandCta }}</button>
            </div>
        </Transition>
    </AppLayout>
</template>

<style>
:root { --sv-bg: #fafafa; --sv-dark: #0a0a0a; --sv-text: #222; --sv-muted: #666; --sv-accent: #00d782; --sv-border: #e5e5e5; --sv-font: 'Inter', ui-sans-serif, system-ui, sans-serif; }
</style>

<style scoped>
.sv-container { max-width: 1200px; margin: 0 auto; padding: 0 32px; }
.sv-section-title { font-family: var(--sv-font); font-size: clamp(28px, 3.5vw, 42px); font-weight: 800; letter-spacing: -0.03em; color: var(--sv-text); margin-bottom: 48px; }

.sv-hero { position: relative; min-height: 100svh; display: flex; align-items: center; overflow: hidden; background: var(--sv-dark); }
.sv-hero__bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.sv-hero__overlay { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%); z-index: 1; }
.sv-hero__inner { position: relative; z-index: 2; max-width: 1200px; margin: 0 auto; padding: 140px 32px 80px; width: 100%; }
.sv-hero__content { max-width: 640px; }
.sv-hero__tag { font-family: var(--sv-font); font-size: 13px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: var(--sv-accent); margin-bottom: 24px; }
.sv-hero__h1 { font-family: var(--sv-font); font-size: clamp(36px, 5vw, 56px); font-weight: 800; line-height: 1.1; color: #fff; letter-spacing: -0.03em; margin-bottom: 20px; }
.sv-hero__accent { color: var(--sv-accent); }
.sv-hero__sub { font-family: var(--sv-font); font-size: 18px; line-height: 1.6; color: rgba(255,255,255,0.7); margin-bottom: 32px; }
.sv-btn { font-family: var(--sv-font); font-size: 15px; font-weight: 600; padding: 14px 32px; border: none; border-radius: 8px; cursor: pointer; transition: transform 150ms ease, box-shadow 150ms ease; display: inline-flex; align-items: center; gap: 8px; text-decoration: none; }
.sv-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,0,0,0.15); }
.sv-btn--accent { background: var(--sv-accent); color: var(--sv-dark); }
.sv-btn--dark { background: var(--sv-dark); color: #fff; }
.sv-btn--full { width: 100%; justify-content: center; }

.sv-benefits { background: var(--sv-bg); padding: 100px 0; }
.sv-benefits__grid { display: grid; grid-template-columns: 1fr; gap: 20px; }
@media (min-width: 768px) { .sv-benefits__grid { grid-template-columns: repeat(2, 1fr); } }
.sv-benefits__card { padding: 36px 32px; border-radius: 16px; background: #fff; border: 1px solid var(--sv-border); position: relative; transition: box-shadow 300ms ease; }
.sv-benefits__card:hover { box-shadow: 0 8px 32px rgba(0,0,0,0.06); }
.sv-benefits__card::before { content: ''; position: absolute; inset: -1px; border-radius: 17px; background: linear-gradient(135deg, var(--sv-accent), #FBCA8D); z-index: -1; opacity: 0; transition: opacity 300ms ease; }
.sv-benefits__card:hover::before { opacity: 1; }
.sv-benefits__num { font-family: var(--sv-font); font-size: 13px; font-weight: 700; color: var(--sv-accent); letter-spacing: 0.05em; display: block; margin-bottom: 12px; }
.sv-benefits__card h3 { font-family: var(--sv-font); font-size: 20px; font-weight: 700; color: var(--sv-text); margin-bottom: 8px; }
.sv-benefits__card p { font-family: var(--sv-font); font-size: 15px; line-height: 1.6; color: var(--sv-muted); margin: 0; }

.sv-reviews { background: #fff; padding: 100px 0; }
.sv-reviews__track { display: flex; gap: 20px; overflow-x: auto; scroll-snap-type: x mandatory; padding: 0 32px 16px; scrollbar-width: none; }
.sv-reviews__track::-webkit-scrollbar { display: none; }
.sv-reviews__card { flex: 0 0 clamp(280px, 35vw, 400px); scroll-snap-align: start; background: var(--sv-bg); border-radius: 12px; padding: 32px 28px; }
.sv-reviews__text { font-family: var(--sv-font); font-size: 15px; line-height: 1.7; color: var(--sv-text); margin-bottom: 16px; }
.sv-reviews__author { font-family: var(--sv-font); font-size: 14px; color: var(--sv-text); }
.sv-reviews__author span { color: var(--sv-muted); font-weight: 400; }

.sv-cta-section { background: var(--sv-accent); padding: 100px 0; }
.sv-cta-section h2 { font-family: var(--sv-font); font-size: clamp(28px, 4vw, 44px); font-weight: 800; letter-spacing: -0.03em; color: var(--sv-dark); margin-bottom: 32px; }

.sv-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(6px); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 24px; }
.sv-dialog { background: #fff; border-radius: 16px; width: 100%; max-width: 520px; max-height: 90vh; overflow-y: auto; padding: 36px 28px; position: relative; }
.sv-dialog__x { position: absolute; top: 14px; right: 14px; width: 36px; height: 36px; border: none; background: #f5f5f5; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--sv-dark); }
.sv-dialog__title { font-family: var(--sv-font); font-size: 20px; font-weight: 700; color: var(--sv-dark); text-align: center; margin-bottom: 4px; }
.sv-dialog__sub { font-family: var(--sv-font); font-size: 14px; color: var(--sv-muted); text-align: center; margin-bottom: 24px; }
.sv-fade-enter-active, .sv-fade-leave-active { transition: opacity 250ms ease; }
.sv-fade-enter-from, .sv-fade-leave-to { opacity: 0; }
.sv-sticky { position: fixed; bottom: 0; left: 0; right: 0; padding: 12px 16px; background: #fff; box-shadow: 0 -4px 20px rgba(0,0,0,0.08); z-index: 900; }
.sv-slide-enter-active, .sv-slide-leave-active { transition: transform 250ms ease; }
.sv-slide-enter-from, .sv-slide-leave-to { transform: translateY(100%); }
@media (max-width: 767px) { .sv-hero__inner { padding: 120px 24px 60px; } }
</style>
